'use client'

import { parseCookies } from 'nookies'
import { useCallback, useEffect, useRef } from 'react'

import { SESSION_COOKIE_IP, SESSION_EXPIRES_AT_COOKIE_NAME } from '~/core/constants/cookies'
import { setSessionCookieClient } from '~/core/middleware/save-session-cookie'

import { useNetworkChange } from '~/lib/hooks/use-network-change'

// ✅ OPTIMIZE: Cache key for tracking last fetch time
const LAST_IP_FETCH_KEY = 'lastIpFetchTime'
// ✅ OPTIMIZE: Minimum time between fetches (30 minutes)
const MIN_FETCH_INTERVAL = 30 * 60 * 1000 // 30 minutes in milliseconds

const NetworkWatcher = () => {
  const cookies = parseCookies()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isFetchingRef = useRef(false)

  const fetchIp = useCallback(async () => {
    // ✅ OPTIMIZE: Prevent concurrent fetches
    if (isFetchingRef.current) {
      return
    }

    // ✅ OPTIMIZE: Check if we have a valid IP already
    const existingIp = cookies[SESSION_COOKIE_IP]
    if (existingIp && existingIp !== 'unknown' && existingIp !== '[object Object]') {
      // Check last fetch time to avoid fetching too frequently
      const lastFetchTime = localStorage.getItem(LAST_IP_FETCH_KEY)
      if (lastFetchTime) {
        const timeSinceLastFetch = Date.now() - parseInt(lastFetchTime, 10)
        // If we fetched recently and have a valid IP, skip
        if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
          return
        }
      }
    }

    isFetchingRef.current = true

    try {
      const res = await fetch('/api/getip')

      // ✅ FIX: Handle rate limit (429) and validate response
      if (!res.ok) {
        // If rate limited or other error, don't update IP cookie
        // Keep existing IP if available
        return
      }

      const ipValue = await res.json()

      // ✅ FIX: Validate IP value before setting cookie
      // Ensure it's a valid string and not an error object
      if (
        typeof ipValue === 'string' &&
        ipValue !== 'unknown' &&
        ipValue !== '[object Object]' &&
        !ipValue.startsWith('{') // Not a JSON error object
      ) {
        setSessionCookieClient(
          SESSION_COOKIE_IP,
          ipValue,
          Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
        )
        // ✅ OPTIMIZE: Track successful fetch time
        localStorage.setItem(LAST_IP_FETCH_KEY, Date.now().toString())
      }
    } catch (error) {
      // Silently fail - IP fetch is not critical
    } finally {
      isFetchingRef.current = false
    }
  }, [cookies])

  // ✅ OPTIMIZE: Debounced fetch IP function with longer delay
  const debouncedFetchIp = useCallback(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // ✅ OPTIMIZE: Wait 5 seconds before fetching on network change
    // This prevents multiple rapid fetches when network flickers
    debounceTimeoutRef.current = setTimeout(() => {
      fetchIp()
    }, 5000) // Debounce 5 seconds
  }, [fetchIp])

  useEffect(() => {
    // ✅ OPTIMIZE: Only fetch on mount if we don't have a valid IP
    const existingIp = cookies[SESSION_COOKIE_IP]
    if (!existingIp || existingIp === 'unknown' || existingIp === '[object Object]') {
      fetchIp()
    }

    // ✅ FIX: Cleanup debounce timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
    // ✅ OPTIMIZE: Only depend on IP cookie, not all cookies to prevent unnecessary re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchIp, cookies[SESSION_COOKIE_IP]])

  // ✅ OPTIMIZE: Only fetch on network change if we're coming back online
  // and it's been a while since last fetch
  useNetworkChange(() => {
    // Only fetch when coming back online
    if (navigator.onLine) {
      debouncedFetchIp()
    }
  })

  return null
}

export default NetworkWatcher
